import { get, isEmpty } from "lodash-es";
import { getNomineeData, getNomineeRelationshipViewValue, getNomineesList } from "../utils/nominee/functions";

export const MAXIMUM_DEMAT_NOMINEES = 3;

export const DEMAT_NOMINEE_STATUS_MAPPER = {
  init: "init",
  incomplete: "incomplete",
  inProgress: "in_progress",
  rejected: "rejected",
  esignReady: "esign_ready",
  approved: "approved",
};

export const DEMAT_NOMINEE_STATUS = [
  DEMAT_NOMINEE_STATUS_MAPPER.init,
  DEMAT_NOMINEE_STATUS_MAPPER.incomplete,
  DEMAT_NOMINEE_STATUS_MAPPER.inProgress,
  DEMAT_NOMINEE_STATUS_MAPPER.rejected,
  DEMAT_NOMINEE_STATUS_MAPPER.esignReady,
  DEMAT_NOMINEE_STATUS_MAPPER.approved,
];

export const getMfNomineeData = (status, mfNominationsData) => () => {
	const isNomineeOptional = get(mfNominationsData, 'nominee_optional', false);
	const MF_STATUS_MAPPER = {
		init: {
			isMfNomineeAdded: false,
			options: [
				{
					subtitle: {
						text: '0 nominees added',
						dataAid: 'key',
					},
				},
			],
		},
		approved: {
			isMfNomineeAdded: true,
		},
	};
	const isStatusInitOrRejected = ['init', 'rejected'].includes(status);
	if (!isNomineeOptional && !isStatusInitOrRejected) {
		status = 'approved';
	}

	let data = MF_STATUS_MAPPER[status] || MF_STATUS_MAPPER['init'];
	const nomineeList = mfNominationsData?.meta_data?.nominee_list || [];

	if (
		!isNomineeOptional &&
		!isStatusInitOrRejected &&
		!isEmpty(nomineeList)
	) {
		const options = nomineeList.map((nominee) => {
			const name = get(nominee, 'name', '');
			const relation = get(nominee, 'relation', '');
			const share = get(nominee, 'share', '');
			const relationship = getNomineeRelationshipViewValue(relation);
			const nomineeData = {
				subtitle: {
					text: name,
					dataAid: 'key',
				},
				share: {
					value: share ? share + '%' : '',
					dataAid: 'value',
				},
				subtext: {
					text: relationship,
					dataAid: 'info',
				},
			};
			return nomineeData;
		});
		data.options = options;
	}
	return data;
};

const DEMAT_STATUS_MAPPER = {
  init: {
    showEdit: false,
    showStatus: false,
    buttonTitle: "Add nominees",
    eventStatus: "no_nominee_added",
    options: [
      {
        subtitle: {
          text: "0 nominees added",
          dataAid: "key",
        },
      },
    ],
  },
  incomplete: {
    showEdit: false,
    statusVariant: "ATTENTION",
    statusTitle: "Incomplete",
    eventStatus: "nominee_incomplete",
    buttonTitle: "COMPLETE APPLICATION",
    statusDataAid: "incomplete",
  },
  rejected: {
    showEdit: false,
    statusVariant: "WARNING",
    statusTitle: "Rejected",
    eventStatus: "nomiee_rejected",
    buttonTitle: "EDIT DETAILS",
    statusDataAid: "rejected",
  },
  in_progress: {
    showEdit: false,
    statusVariant: "ATTENTION",
    eventStatus: "nominee_inprogress",
    statusTitle: "In progress",
    statusDataAid: "incomplete",
  },
  esign_ready: {
    showEdit: false,
    statusVariant: "POSITIVE",
    statusTitle: "ESIGN ready",
    eventStatus: "nominee_esign_pending",
    buttonTitle: "Complete esign",
    statusDataAid: "esignReady",
  },
  approved: {
    showEdit: true,
    eventStatus: "nominee_completed",
    iconDataAid: "right",
    statusDataAid: "rejected",
  },
};

export const getDematNomineeData =
	(status = '', equityNominationsData = {}, kyc = {}) =>
	() => {
  const subbrokerCode = get(kyc, 'subbroker.code', '');
  const subbrokerName = get(kyc, 'subbroker.name', '');
  const isDematNomineeDisabled = ['ubi', 'ubimb', 'ubiweb'].includes(subbrokerCode);
  if (isDematNomineeDisabled) {
    const disabledData = {
      subtitle: `Visit your nearest ${subbrokerName} branch to update nominee details in your Demat account.`,
      dataAid: 'key2',
      showEdit: false,
      showStatus: false,
    }
    return disabledData;
  }
  const data = DEMAT_STATUS_MAPPER[status] || DEMAT_STATUS_MAPPER["init"];
  const nomineeList = getNomineesList(equityNominationsData);

  if (status !== "init") {
    const options = nomineeList.map((el) => {
      const data = getNomineeData(el);
      const relationship = getNomineeRelationshipViewValue(data.relationship);
      const isRejected = data.status === "rejected";
      const nomineeData = {
        subtitle: {
          text: data.name,
          dataAid: "key",
        },
        share: {
          value: data.share ? data.share + "%" : "",
          dataAid: "value",
        },
        subtext: {
          text: relationship,
          dataAid: "info",
        },
        status: {
          text: isRejected ? "Invalid documents" : "",
          dataAid: "invalidDoc",
        },
      };
      return nomineeData;
    });
    data.options = options;
  }

  return data;
};

export const NOMINEE_MENU_OPTIONS = [{ name: "Edit", value: "edit" }];

export const NOMINEE_RELATIONSHIP = [
  {
    label: "Spouse",
    value: "spouse",
  },
  {
    label: "Son",
    value: "son",
  },
  {
    label: "Daughter",
    value: "daughter",
  },
  {
    label: "Father",
    value: "father",
  },
  {
    label: "Mother",
    value: "mother",
  },
  {
    label: "Brother",
    value: "brother",
  },
  {
    label: "Sister",
    value: "sister",
  },
  {
    label: "Grandson",
    value: "grand-son",
  },
  {
    label: "Granddaughter",
    value: "grand-daughter",
  },
  {
    label: "Grandfather",
    value: "grand-father",
  },
  {
    label: "Grandmother",
    value: "grand-mother",
  },
  {
    label: "Others",
    value: "others",
  },
];

export const PERSONAL_DETAILS_FORM_MAPPER = {
  name: "name",
  dob: "dob",
  relationship: "relationship",
  share: "share",
  guardianName: "guardianName",
  guardianRelationship: "guardianRelationship",
  mobile: "mobile",
  email: "email",
  isMinor: "isMinor",
  id: "id",
  status: "status",
};

export const ERROR_MESSAGES = {
  minorDob1: "Nominee must be a minor. Enter correct DOB",
  minorDob2: "Entered DOB is of a minor",
  consecutiveSpaces: "consecutive spaces are not allowed",
  pincode: "Enter the correct PIN code",
  emptyField: "This information is required",
  mobile: "Enter 10 digit mobile number",
  share: "Percentage share should not exceed total available share holding",
  minShare: "Percentage share should be more than 0",
  general: "Please enter valid",
  uploadDocument: "Document upload is mandatory",
  nomineeName: "Nominee name cannot be same as your name",
  guardianName: "Guardian name cannot be same as nominee name",
};

export const NOMINEE_PROOF_OF_IDENTITY = [
  {
    name: "Pan",
    value: "pan",
    numberOfDocs: 1,
  },
  {
    name: "Aadhaar card",
    value: "aadhar",
    numberOfDocs: 2,
  },
  {
    name: "Voter’s id",
    value: "voter_id",
    numberOfDocs: 2,
  },
  {
    name: "Driving license",
    value: "driving_license",
    numberOfDocs: 2,
  },
];

export const ADDRESS_DETAILS_FORM_MAPPER = {
  pincode: "pincode",
  address: "address",
  city: "city",
  state: "state",
  poi: "poi",
  password: "password",
  file: "file",
  frontDoc: "frontDoc",
  backDoc: "backDoc",
  guardianPincode: "guardianPincode",
  guardianAddress: "guardianAddress",
  guardianCity: "guardianCity",
  guardianState: "guardianState",
};

export const DEFAULT_NOMINEE_PERSONAL_DETAILS = {
  [PERSONAL_DETAILS_FORM_MAPPER.name]: "",
  [PERSONAL_DETAILS_FORM_MAPPER.mobile]: "",
  [PERSONAL_DETAILS_FORM_MAPPER.relationship]: "",
  [PERSONAL_DETAILS_FORM_MAPPER.guardianName]: "",
  [PERSONAL_DETAILS_FORM_MAPPER.guardianRelationship]: "",
  [PERSONAL_DETAILS_FORM_MAPPER.mobile]: "",
  [PERSONAL_DETAILS_FORM_MAPPER.email]: "",
  [PERSONAL_DETAILS_FORM_MAPPER.share]: "",
  [PERSONAL_DETAILS_FORM_MAPPER.isMinor]: false,
  [PERSONAL_DETAILS_FORM_MAPPER.id]: "",
};

export const DEFAULT_NOMINEE_ADDRESS_DETAILS = {
  [ADDRESS_DETAILS_FORM_MAPPER.pincode]: "",
  [ADDRESS_DETAILS_FORM_MAPPER.address]: "",
  [ADDRESS_DETAILS_FORM_MAPPER.city]: "",
  [ADDRESS_DETAILS_FORM_MAPPER.state]: "",
  [ADDRESS_DETAILS_FORM_MAPPER.guardianPincode]: "",
  [ADDRESS_DETAILS_FORM_MAPPER.guardianAddress]: "",
  [ADDRESS_DETAILS_FORM_MAPPER.guardianCity]: "",
  [ADDRESS_DETAILS_FORM_MAPPER.guardianState]: "",
  [ADDRESS_DETAILS_FORM_MAPPER.password]: "",
  [ADDRESS_DETAILS_FORM_MAPPER.poi]: "",
  [ADDRESS_DETAILS_FORM_MAPPER.file]: null,
  [ADDRESS_DETAILS_FORM_MAPPER.frontDoc]: null,
  [ADDRESS_DETAILS_FORM_MAPPER.backDoc]: null,
};

export const ESIGN_STEPS = [
  { text: "Verify mobile and enter Aadhaar number", dataAid: "aadhar" },
  {
    text: "Enter OTP recieved on your aadhaar linked mobile number",
    dataAid: "otp",
  },
  { text: "eSign is successfully completed", dataAid: "esign" },
];

export const COMPANY_DETAILS = {
  name: "Finwizard technology Private Ltd.",
  address:
    "Queens Paradise, No. 16/1, 1st Floor, Curve Rd, Shivaji Nagar, Bengaluru, Karnataka 560051",
};

export const MANUAL_SIGNATURE_STEPS = [
  {
    title: "Get all the forms",
    subtitle: "Forms with instructions will be emailed to",
    buttonTitle: "Download & email forms",
  },
  {
    title: "Print all the forms & add signature",
    subtitle: "Sign on all marked boxes",
  },
  {
    title: "Courier the documents to us",
  },
];

export const NOMINEE_OPTIONAL_FLAGS = [
  PERSONAL_DETAILS_FORM_MAPPER.mobile,
  PERSONAL_DETAILS_FORM_MAPPER.email,
];
